/**
 * SmartRedirect - Intelligent post-login redirect
 *
 * Finds the FIRST accessible page by scanning MODULES_CONFIG in sidebar order.
 * Uses the same two-level permission check as the sidebar:
 *   1. Module permission (e.g. 'dashboard.view')
 *   2. Submodule permission (e.g. dashboard.main)
 *
 * This means the user always lands on the first page they see in the sidebar.
 * Works for ANY role, even roles without dashboard access.
 */

import { useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/shared/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'
import { MODULES_CONFIG } from '@/config/modules-config'
import { AccessDeniedPage } from '@/shared/components/AccessDeniedPage'

type BackendPermissions = Record<string, Record<string, boolean>>

export function SmartRedirect() {
  const { user } = useAuth()

  // Broker users always go to their portal — they don't use company-level
  // permissions so the normal MODULES_CONFIG scan would land them on the
  // wrong page (e.g. Dashboard General).
  if (user?.role === 'owly_broker') {
    return <Navigate to="/dashboard/broker" replace />
  }

  // Fetch company-level permissions (no project context for redirect)
  const { data: permissions, isLoading } = useQuery<BackendPermissions>({
    queryKey: ['permissions', 'company-only', user?.email] as const,
    queryFn: async () => {
      const data = await apiClient.get<BackendPermissions>('/auth/permissions/me/')
      return (data as BackendPermissions) || {}
    },
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Find first accessible page - same logic as sidebar
  const firstAccessibleHref = useMemo(() => {
    if (!permissions) return null

    for (const module of MODULES_CONFIG) {
      // 1. Check module-level permission
      if (module.requiredPermission) {
        const [mod, action] = module.requiredPermission.split('.')
        if (permissions[mod]?.[action] !== true) continue
      }

      // 2. Find first item with submodule permission
      for (const item of module.items) {
        // Check item-level permission
        if (item.requiredPermission) {
          const [mod, action] = item.requiredPermission.split('.')
          if (permissions[mod]?.[action] !== true) continue
        }

        // Check submodule permission (module.id + item.id)
        if (permissions[module.id]?.[item.id] === true) {
          return item.href
        }
      }
    }

    return null
  }, [permissions])

  // Loading state
  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Cargando...
          </p>
        </div>
      </div>
    )
  }

  // Redirect to first accessible page
  if (firstAccessibleHref) {
    return <Navigate to={firstAccessibleHref} replace />
  }

  // No permissions at all
  return (
    <AccessDeniedPage
      message="No tienes permisos asignados. Contacta a tu administrador para obtener acceso."
    />
  )
}
